import { useRef, useState } from 'react';
import type { ChangeEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { X, Upload } from 'lucide-react';
import toast from 'react-hot-toast';
import { useQueryClient } from '@tanstack/react-query';
import { Modal } from '~/components/overlays';
import { Button } from '~/components/ui';
import { useMakeRequest } from '~/hooks/useMakeRequest';
import { DOCUMENT_TYPES, formatFileSize } from '~/utils/documentType';
import type { DocumentType } from '~/graphql/graphql';
import { MAX_UPLOAD_SIZE_MB, uploadErrorKey } from '~/utils/uploadError';

interface UploadDocumentModalProps {
  isOpen: boolean;
  onClose: () => void;
  orderId: string;
  onUploadSuccess?: () => void;
}

export function UploadDocumentModal({
  isOpen,
  onClose,
  orderId,
  onUploadSuccess,
}: Readonly<UploadDocumentModalProps>) {
  const { t } = useTranslation('order');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [documentType, setDocumentType] =
    useState<DocumentType>('COMMERCIAL_INVOICE');
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { makeRequest: uploadDocument, loading: isUploading } =
    useMakeRequest();
  const queryClient = useQueryClient();

  // Refused here so an oversized file is never sent at all, the way DragAndDropInput does it.
  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0] ?? null;
    if (file && file.size > MAX_UPLOAD_SIZE_MB * 1024 * 1024) {
      toast.error(
        t('general:drag_and_drop.size_error', { maxSizeMB: MAX_UPLOAD_SIZE_MB })
      );
      event.target.value = '';
      setSelectedFile(null);
      return;
    }
    setSelectedFile(file);
  };

  const handleChooseFile = () => {
    fileInputRef.current?.click();
  };

  const handleClose = () => {
    if (isUploading) return;
    setSelectedFile(null);
    setDocumentType('COMMERCIAL_INVOICE');
    if (fileInputRef.current) fileInputRef.current.value = '';
    onClose();
  };

  const handleUpload = () => {
    if (!selectedFile) return;
    const formData = new FormData();
    formData.append('file', selectedFile);
    formData.append('documentType', documentType);
    uploadDocument(
      `/api/orders/${orderId}/documents`,
      'POST',
      formData,
      () => {
        queryClient.invalidateQueries({ queryKey: ['orders', orderId] });
        queryClient.invalidateQueries({ queryKey: ['packingList', orderId] });
        toast.success(t('order.documents.upload_modal.upload_success'));
        handleClose();
        onUploadSuccess?.();
      },
      (error) => {
        toast.error(
          t(
            uploadErrorKey(error.response?.data) ??
              'order.documents.upload_modal.upload_error',
            { maxSizeMB: MAX_UPLOAD_SIZE_MB }
          )
        );
      }
    );
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose}>
      <div className="w-full max-w-lg rounded-2xl bg-neut-0 shadow-2xl">
        <div className="flex items-start justify-between gap-4 border-b border-neut-100 p-6">
          <div>
            <h3 className="text-lg font-semibold text-neut-prim">
              {t('order.documents.upload_modal.title')}
            </h3>
            <p className="mt-1 text-sm text-neut-sec font-normal">
              {t('order.documents.upload_modal.description')}
            </p>
          </div>
          <button
            type="button"
            onClick={handleClose}
            disabled={isUploading}
            aria-label={t('order.documents.upload_modal.close_aria_label')}
            className="rounded-lg p-2 text-neut-sec transition-colors hover:bg-neut-100 hover:text-neut-prim disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <X size={16} aria-hidden="true" />
          </button>
        </div>

        <div className="space-y-5 p-6">
          <div>
            <label
              htmlFor="document-type-select"
              className="block text-xs font-semibold uppercase tracking-wide text-neut-sec mb-2"
            >
              {t('order.documents.upload_modal.document_type_label')}
            </label>
            <select
              id="document-type-select"
              value={documentType}
              onChange={(event) =>
                setDocumentType(event.target.value as DocumentType)
              }
              className="w-full rounded-lg border border-neut-200 bg-neut-0 px-3 py-2.5 text-sm text-neut-prim focus:outline-none focus:ring-2 focus:ring-prim-200"
            >
              {DOCUMENT_TYPES.map((type) => (
                <option key={type} value={type}>
                  {t(`order.documents.types.${type}`)}
                </option>
              ))}
            </select>
          </div>

          <div className="rounded-xl border border-dashed border-neut-200 bg-neut-50 p-4">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
              <div className="min-w-0">
                {selectedFile ? (
                  <>
                    <p className="truncate text-sm font-medium text-neut-prim">
                      {selectedFile.name}
                    </p>
                    <p className="mt-1 text-xs text-neut-sec">
                      {t(`order.documents.types.${documentType}`)} —{' '}
                      {formatFileSize(selectedFile.size)}
                    </p>
                  </>
                ) : (
                  <>
                    <p className="text-sm font-medium text-neut-prim">
                      {t('order.documents.upload_modal.no_file_selected')}
                    </p>
                    <p className="mt-1 text-xs text-neut-sec">
                      {t('order.documents.upload_modal.supported_formats')}
                    </p>
                  </>
                )}
              </div>
              <button
                type="button"
                onClick={handleChooseFile}
                disabled={isUploading}
                className="rounded-lg border border-prim-200 px-4 py-2.5 text-sm font-medium text-prim-700 transition-colors hover:bg-prim-50 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {t('order.documents.upload_modal.choose_file')}
              </button>
            </div>
          </div>

          <input
            ref={fileInputRef}
            type="file"
            accept=".pdf,.doc,.docx,.xls,.xlsx,.csv,.jpg,.jpeg,.png"
            className="hidden"
            onChange={handleFileChange}
          />

          <div className="flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
            <Button
              variant="accent"
              size="sm"
              width="auto"
              onClick={handleClose}
              disabled={isUploading}
            >
              {t('order.documents.upload_modal.cancel')}
            </Button>
            <Button
              size="sm"
              width="auto"
              onClick={handleUpload}
              disabled={!selectedFile || isUploading}
              loading={isUploading}
            >
              <Upload size={16} aria-hidden="true" />
              {t('order.documents.upload_modal.upload_button')}
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  );
}
